import { Injectable, inject } from '@angular/core';
import { FsErrorResponse, FsTaskService } from '@fairandsmart/angular';
import {
  CreateTaskDto,
  EntityCollection,
  EntityName,
  EntityReference,
  Task,
  UpdateTaskDto,
} from '@fairandsmart/types';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { AlertService } from '@Services/alert.service';
import { OrgaService } from '@Services/orga.service';
import {
  AddCommentSuccess,
  AssignTask,
  CancelTask,
  CloseTaskPanel,
  CompleteTask,
  CountUserTasksToDo,
  CreateRequestTask,
  CreateTaskSuccess,
  LoadRefTasks,
  QuitEntityPage,
  RefreshTasks,
  SelectRefTab,
  SelectUserTab,
  ShowRequestTasks,
  ShowTaskDetail,
  ShowUserTasks,
  UpdateTaskSuccess,
} from './task.actions';
import {
  selectRefTasks, selectUserTask, taskPanelOpened, taskPanelTab, userTodos,
} from './task.reducer';
import { CallState, FsOrgState, TaskPanelTab } from '../../../reducers';

@Injectable()
export class TaskService {
  readonly store = inject<Store<FsOrgState>>(Store);

  readonly fsTaskService = inject(FsTaskService);

  readonly orgaService = inject(OrgaService);

  readonly alert = inject(AlertService);

  public userTodos$: Observable<number>;

  public refTasks$: Observable<{ reference: EntityReference, type: EntityName, collection: EntityCollection<Task>, state: CallState }>;

  public userTasks$: Observable<{ collection: EntityCollection<Task>, state: CallState }>;

  public panelOpened$: Observable<boolean>;

  public panelTab$: Observable<{ current: TaskPanelTab, type: EntityName | null }>;

  constructor() {
    this.userTodos$ = this.store.select(userTodos);
    this.userTasks$ = this.store.select(selectUserTask);
    this.refTasks$ = this.store.select(selectRefTasks);
    this.panelTab$ = this.store.select(taskPanelTab);
    this.panelOpened$ = this.store.select(taskPanelOpened);
  }

  public countUserTodos(): void {
    this.store.dispatch(new CountUserTasksToDo());
  }

  public selectUserTab(): void {
    this.store.dispatch(new SelectUserTab());
  }

  public selectRefTab(): void {
    this.store.dispatch(new SelectRefTab());
  }

  public showUserTasks(): void {
    this.store.dispatch(new ShowUserTasks());
  }

  public showRequestTasks(): void {
    this.store.dispatch(new ShowRequestTasks());
  }

  public loadRefTasks(reference: EntityReference): void {
    this.store.dispatch(new LoadRefTasks({ reference }));
  }

  public closePanel(): void {
    this.store.dispatch(new CloseTaskPanel());
  }

  public refreshTasks(): void {
    this.store.dispatch(new RefreshTasks());
  }

  public quitEntityPage(): void {
    this.store.dispatch(new QuitEntityPage());
  }

  public assignTask(task: Task, assignee: string): void {
    this.store.dispatch(new AssignTask({ taskId: task.id, assignee, previousAssignee: task.assignee }));
  }

  public cancelTask(task: Task, variables?: { [key: string]: string }): void {
    this.store.dispatch(new CancelTask({ task, variables }));
  }

  public completeTask(task: Task, variables?: { [key: string]: string }): void {
    this.store.dispatch(new CompleteTask({ task, variables }));
  }

  public showTaskDetail(task: Task): void {
    this.store.dispatch(new ShowTaskDetail({ task }));
  }

  public createRefTask(): void {
    this.refTasks$.pipe(
      take(1),
      tap((value) => {
        this.store.dispatch(new CreateRequestTask({ reference: value.reference }));
      }),
    ).subscribe();
  }

  public createRequestTask(reference: EntityReference): void {
    this.store.dispatch(new CreateRequestTask({ reference }));
  }

  public create(dto: CreateTaskDto): Observable<Task> {
    return this.fsTaskService.create(dto).pipe(
      tap((task: Task) => {
        this.alert.success('TASK.ALERT.CREATE_SUCCESS');
        this.store.dispatch(new CreateTaskSuccess({ task }));
      }),
      catchError((error) => {
        this.alert.error('TASK.ALERT.CREATE_ERROR', error);
        return throwError(error);
      }),
    );
  }

  public update(task: Task, dto: UpdateTaskDto): Observable<Task> {
    return this.fsTaskService.update(task.id, dto).pipe(
      tap((updatedTask: Task) => {
        this.alert.success('TASK.ALERT.UPDATE_SUCCESS');
        this.store.dispatch(new UpdateTaskSuccess({ task: updatedTask }));
      }),
      catchError((error) => {
        this.alert.error('TASK.ALERT.UPDATE_ERROR', error);
        return throwError(error);
      }),
    );
  }

  public comment(id: string, message: string): Observable<Task> {
    return this.fsTaskService.addComment(id, message).pipe(
      tap((updatedTask: Task) => {
        this.alert.success('TASK.ALERT.COMMENT_SUCCESS');
        this.store.dispatch(new AddCommentSuccess({ task: updatedTask }));
      }),
      catchError((response: FsErrorResponse) => {
        this.alert.apiError(response.error, null);
        return throwError(response);
      }),
    );
  }

  public goToTaskReference(task: Task): void {
    this.orgaService.viewEntityDetail(task.reference);
  }
}
